import React, { useState, useEffect, useRef } from 'react';
import { 
  Flame, X, Camera, Upload, 
  Send, AlertCircle,
  ChevronRight, Share2,
  Crosshair, Eye, Shirt as ShirtIcon, Scan
} from 'lucide-react';

import { getAuth, User as FirebaseUser } from 'firebase/auth';
import { collection, doc, setDoc, updateDoc, onSnapshot, getDocs, deleteDoc, increment, arrayUnion } from 'firebase/firestore';
import { db, auth } from '../../services/firebase'; // Ensure this path matches your project structure
import { useAuth } from '../../hooks/useAuth'; // Same hook MysteryDropModal uses

// -----------------------------------------------------------------------------
// compressImage
// -----------------------------------------------------------------------------
const compressImage = async (dataUrl: string, maxWidth = 500): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = dataUrl;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const scale = Math.min(1, maxWidth / img.width);
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.6));
    };
    img.onerror = () => {
      resolve(dataUrl);
    };
  });
};

// -----------------------------------------------------------------------------
// Random outfit rating roll — this is the sole "judge". Not AI, pure RNG.
// Rolled ONCE by whichever client's write pairs the match, so both players
// see identical numbers instead of each client rolling its own.
// -----------------------------------------------------------------------------
interface OutfitRating {
  pants: number;
  shirt: number;
  fullFit: number;
  final: number;
}

const rollOutfitScore = (): OutfitRating => {
  const roll = () => Math.round((Math.random() * 9 + 1) * 10) / 10; // 1.0 - 10.0
  const pants = roll();
  const shirt = roll();
  const fullFit = roll();
  const final = Math.round(((pants + shirt + fullFit) / 3) * 10) / 10;
  return { pants, shirt, fullFit, final };
};

const appId = 'scoophype-thread-wars'; // Your app identifier

type GameState = 'lobby' | 'setup' | 'matching' | 'intro' | 'playing' | 'result';
type InputMode = 'camera' | 'upload' | null;

interface BattleUser {
  uid: string;
  name: string;
  media: string;
}

interface FireMatch {
  id?: string;
  status: 'waiting' | 'playing' | 'result';
  userA: BattleUser;
  userB: BattleUser | null;
  ratingA: OutfitRating | null;
  ratingB: OutfitRating | null;
  startTime: number | null;
  comments: { user: string, text: string }[];
  createdAt: number;
}

export const ThreadForThread = ({ onClose, displayName = "HYPE_USER", startAsSpectator = false }: any) => {
  // Same auth pattern as MysteryDropModal — pulled from the hook, not a
  // prop, so it's always the live logged-in user rather than whatever
  // (possibly stale/never-passed) value a parent might hand down.
  const { user } = useAuth();

  const [gameState, setGameState] = useState<GameState>('lobby');
  const [inputMode, setInputMode] = useState<InputMode>(null);
  const [notification, setNotification] = useState<string | null>(null);
  const [showCameraConsent, setShowCameraConsent] = useState(false);
  
  const [userMediaStream, setUserMediaStream] = useState<MediaStream | null>(null);
  const [userPhotoUrl, setUserPhotoUrl] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Captured + compressed outfit photo, cached so "Next Battle" can requeue
  // instantly without re-capturing from the camera/upload.
  const [myMediaBase64, setMyMediaBase64] = useState<string | null>(null);
  
  const [currentMatchId, setCurrentMatchId] = useState<string | null>(null);
  const [matchData, setMatchData] = useState<FireMatch | null>(null);
  const [newComment, setNewComment] = useState('');
  const [timeLeft, setTimeLeft] = useState(15);

  const showToast = (msg: string) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 3000);
  };

  useEffect(() => {
    return () => {
      if (userMediaStream) userMediaStream.getTracks().forEach(track => track.stop());
      if (currentMatchId && matchData?.status === 'waiting' && user && matchData.userA.uid === user.uid) {
        deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'thread_matches', currentMatchId)).catch(console.error);
      }
    };
  }, [userMediaStream, currentMatchId, matchData, user]);

  useEffect(() => {
    if (!currentMatchId || !user) return;

    const matchRef = doc(db, 'artifacts', appId, 'public', 'data', 'thread_matches', currentMatchId);
    const unsubscribe = onSnapshot(matchRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data() as FireMatch;
        setMatchData({ id: docSnap.id, ...data });

        if (data.status === 'playing' && gameState === 'matching') {
          setGameState('intro');
          setTimeout(() => setGameState('playing'), 3000);
        } else if (data.status === 'result' && (gameState === 'playing' || gameState === 'intro')) {
          setGameState('result');
        }

        if (data.status === 'playing' && data.startTime) {
          const elapsed = Math.floor((Date.now() - data.startTime) / 1000);
          const remaining = Math.max(0, 15 - elapsed);
          setTimeLeft(remaining);
        }
      } else {
        if (gameState === 'matching' || gameState === 'playing' || gameState === 'intro') {
           showToast("Connection to opponent lost.");
           resetGame();
        }
      }
    }, (error) => {
      console.error("Match sync error:", error);
    });

    return () => unsubscribe();
  }, [currentMatchId, gameState, user]);

  // Resolve the match once the countdown ends. Still authored by userA's
  // client (same single-writer pattern as before) — ratings were already
  // rolled at pairing time, so this just seals the result + applies ELO.
  useEffect(() => {
    if (gameState === 'playing' && matchData?.startTime && timeLeft > 0) {
      const timer = setInterval(() => {
        const elapsed = Math.floor((Date.now() - matchData.startTime!) / 1000);
        const remaining = Math.max(0, 15 - elapsed);
        setTimeLeft(remaining);
        
        if (remaining <= 0) {
           clearInterval(timer);
           if (user && matchData.userA.uid === user.uid && matchData.ratingA && matchData.ratingB) {
             const iWon = matchData.ratingA.final > matchData.ratingB.final;
             const isTie = matchData.ratingA.final === matchData.ratingB.final;
             const eloChange = 26;

             updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'thread_matches', currentMatchId!), {
               status: 'result'
             }).catch(console.error);

             updateDoc(doc(db, 'users', matchData.userA.uid), {
                elo: increment(isTie ? 0 : (iWon ? eloChange : -eloChange)),
             }).catch(console.error);

             if (matchData.userB) {
                updateDoc(doc(db, 'users', matchData.userB.uid), {
                  elo: increment(isTie ? 0 : (!iWon ? eloChange : -eloChange)),
                }).catch(console.error);
             }
           }
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [gameState, matchData, timeLeft, user, currentMatchId]);

  // The <video> element only mounts AFTER inputMode becomes 'camera' (a state
  // update that hasn't re-rendered yet inside startCamera itself), so setting
  // videoRef.current.srcObject directly inside startCamera silently no-ops —
  // the ref is still null at that point. Bind it here instead, once the
  // element actually exists and userMediaStream is set.
  useEffect(() => {
    if (userMediaStream && videoRef.current) {
      videoRef.current.srcObject = userMediaStream;
    }
  }, [userMediaStream, inputMode]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      setUserMediaStream(stream);
      setInputMode('camera');
    } catch (err: any) {
      console.error("Camera error:", err?.name, err?.message, err);
      const reason =
        err?.name === 'NotAllowedError' ? 'Permission blocked in browser settings.'
        : err?.name === 'NotFoundError' ? 'No camera device detected.'
        : err?.name === 'NotReadableError' ? 'Camera is in use by another app/tab.'
        : err?.name === 'OverconstrainedError' ? 'No camera matches the requested settings.'
        : err?.message || 'Unknown error.';
      showToast(`Camera error (${err?.name || 'Unknown'}): ${reason} Try Upload Mode.`);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setUserPhotoUrl(url);
      setInputMode('upload');
    }
  };

  const captureFinalMedia = async (): Promise<string> => {
    if (inputMode === 'camera' && videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      canvas.getContext('2d')?.drawImage(videoRef.current, 0, 0);
      return await compressImage(canvas.toDataURL('image/jpeg'));
    } else if (inputMode === 'upload' && userPhotoUrl) {
      const blob = await fetch(userPhotoUrl).then(r => r.blob());
      const reader = new FileReader();
      const rawBase64 = await new Promise<string>((resolve) => {
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
      });
      return await compressImage(rawBase64);
    }
    return 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?q=80&w=800';
  };

  // reuseMedia=true skips re-capturing and reuses myMediaBase64 (used by
  // "Next Battle" so we don't need a live camera frame every round).
  const startMatchmaking = async (reuseMedia: boolean = false) => {
    console.log('[ThreadForThread] Find Match clicked', { reuseMedia, hasCurrentUser: !!user, inputMode });
    if (!user) {
      console.warn('startMatchmaking: useAuth() returned no user — auth not resolved yet.');
      showToast("Not signed in yet — can't start a match.");
      return;
    }
    if (!reuseMedia && !inputMode) {
      showToast("Pick a camera or upload photo first.");
      return;
    }

    setGameState('matching');
    setMatchData(null);
    setCurrentMatchId(null);

    const mediaBase64 = reuseMedia && myMediaBase64 ? myMediaBase64 : await captureFinalMedia();
    setMyMediaBase64(mediaBase64);
    const myUserData: BattleUser = { uid: user.uid, name: displayName, media: mediaBase64 };

    try {
      const matchesRef = collection(db, 'artifacts', appId, 'public', 'data', 'thread_matches');
      const matchesSnap = await getDocs(matchesRef);
      
      let availableMatchId: string | null = null;
      matchesSnap.forEach(docSnap => {
         const data = docSnap.data() as FireMatch;
         if (data.status === 'waiting' && data.userA.uid !== user.uid && !availableMatchId) {
            if (Date.now() - data.createdAt < 5 * 60 * 1000) {
                availableMatchId = docSnap.id;
            }
         }
      });

      if (availableMatchId) {
        // Joining an existing waiting match: roll BOTH outfit ratings right
        // here, in this single write, so userA and userB see identical numbers.
        const ratingA = rollOutfitScore();
        const ratingB = rollOutfitScore();
        const matchDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'thread_matches', availableMatchId);
        await updateDoc(matchDocRef, {
          userB: myUserData,
          status: 'playing',
          startTime: Date.now() + 3000,
          ratingA,
          ratingB,
        });
        setCurrentMatchId(availableMatchId);
      } else {
        const newMatchRef = doc(matchesRef); 
        await setDoc(newMatchRef, {
           status: 'waiting', userA: myUserData, userB: null,
           ratingA: null, ratingB: null,
           startTime: null, comments: [], createdAt: Date.now()
        });
        setCurrentMatchId(newMatchRef.id);
      }
    } catch (err: any) {
      console.error("Matchmaking failed:", err?.code, err?.message, err);
      showToast(`Grid connection failed: ${err?.code || err?.message || 'unknown error'}`);
      setGameState(reuseMedia ? 'result' : 'setup');
    }
  };

  const handleSpectate = async () => {
    setGameState('matching');
    setMatchData(null);
    setCurrentMatchId(null);

    try {
      const matchesRef = collection(db, 'artifacts', appId, 'public', 'data', 'thread_matches');
      const matchesSnap = await getDocs(matchesRef);
      
      let activeMatchId: string | null = null;
      matchesSnap.forEach(docSnap => {
         const data = docSnap.data() as FireMatch;
         if (data.status === 'playing' && !activeMatchId) {
            activeMatchId = docSnap.id;
         }
      });

      if (activeMatchId) {
        setCurrentMatchId(activeMatchId);
      } else {
        showToast("No active battles right now.");
        setGameState('lobby');
      }
    } catch (err) {
      showToast("Failed to scan the grid.");
      setGameState('lobby');
    }
  };

  useEffect(() => {
    if (startAsSpectator && gameState === 'lobby') handleSpectate();
  }, [startAsSpectator, gameState]);

  const handleSendComment = (e?: React.FormEvent, customText?: string) => {
    if (e) e.preventDefault();
    const textToSend = customText || newComment;
    if (!textToSend.trim() || !currentMatchId) return;
    
    const matchRef = doc(db, 'artifacts', appId, 'public', 'data', 'thread_matches', currentMatchId);
    updateDoc(matchRef, { comments: arrayUnion({ user: displayName, text: textToSend }) }).catch(console.error);
    if (!customText) setNewComment('');
  };

  // Full reset — back to the lobby, forgets the captured photo.
  const resetGame = () => {
    setCurrentMatchId(null);
    setMatchData(null);
    setMyMediaBase64(null);
    setGameState('lobby');
  };

  // Omegle-style "Next" — keep the same photo, requeue immediately.
  const nextBattle = () => {
    setCurrentMatchId(null);
    setMatchData(null);
    startMatchmaking(true);
  };

  const amISpectator = matchData && user ? (matchData.userA.uid !== user.uid && matchData.userB?.uid !== user.uid) : false;

  // Staged reveal during the 15s countdown, purely cosmetic — the ratings
  // themselves are already decided and stored in Firestore the moment the
  // match paired, so latecomer spectators immediately see whatever stage
  // the timer implies.
  const revealStage: 'scanning' | 'subscores' | 'full' =
    timeLeft > 10 ? 'scanning' : timeLeft > 4 ? 'subscores' : 'full';

  return (
    <div className="fixed inset-0 z-[999] bg-[#050505] text-white flex flex-col font-sans overflow-hidden">
      <style>{`
        .bg-cyber-grid {
          background-size: 40px 40px;
          background-image: 
            linear-gradient(to right, rgba(34, 211, 238, 0.05) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(34, 211, 238, 0.05) 1px, transparent 1px);
          animation: panGrid 20s linear infinite;
        }
        @keyframes panGrid {
          0% { background-position: 0 0; }
          100% { background-position: 40px 40px; }
        }
        .scanlines {
          background: linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0) 50%, rgba(0,0,0,0.15) 50%, rgba(0,0,0,0.15));
          background-size: 100% 4px;
          position: absolute; inset: 0; pointer-events: none; z-index: 999;
        }
        
        .neon-text-cyan { text-shadow: 0 0 10px rgba(34, 211, 238, 0.8), 0 0 20px rgba(34, 211, 238, 0.4); }
        .neon-text-yellow { text-shadow: 0 0 10px rgba(250, 204, 21, 0.8), 0 0 20px rgba(250, 204, 21, 0.4); }
        .neon-text-red { text-shadow: 0 0 10px rgba(239, 68, 68, 0.8), 0 0 20px rgba(239, 68, 68, 0.4); }

        .hex-clip { clip-path: polygon(10% 0, 90% 0, 100% 50%, 90% 100%, 10% 100%, 0 50%); }
        .angle-clip { clip-path: polygon(15px 0, 100% 0, calc(100% - 15px) 100%, 0 100%); }
        .hud-border { box-shadow: inset 0 0 20px rgba(34, 211, 238, 0.1), 0 0 15px rgba(34, 211, 238, 0.2); border: 1px solid rgba(34, 211, 238, 0.4); }
        .hud-border-yellow { box-shadow: inset 0 0 20px rgba(250, 204, 21, 0.1), 0 0 15px rgba(250, 204, 21, 0.2); border: 1px solid rgba(250, 204, 21, 0.4); }

        @keyframes ping-slow { 0% { transform: scale(1); opacity: 0.8; } 100% { transform: scale(2.5); opacity: 0; } }
        .animate-ping-slow { animation: ping-slow 1.5s cubic-bezier(0, 0, 0.2, 1) infinite; }
        
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fadeIn 0.5s ease-out forwards; }

        @keyframes popIn { 0% { transform: scale(0.4); opacity: 0; } 70% { transform: scale(1.1); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
        .animate-pop-in { animation: popIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
        
        @keyframes slamIn { 0% { transform: scale(3) rotate(-10deg); opacity: 0; filter: blur(10px); } 70% { transform: scale(0.9) rotate(5deg); opacity: 1; filter: blur(0px); } 100% { transform: scale(1) rotate(0deg); opacity: 1; filter: blur(0px); } }
        .animate-slam-in { animation: slamIn 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
        
        @keyframes slideRight { from { transform: translateX(-100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        .animate-slide-right { animation: slideRight 0.5s ease-out forwards; }
        
        @keyframes slideLeft { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        .animate-slide-left { animation: slideLeft 0.5s ease-out forwards; }

        @keyframes scanSweep { 0% { top: 0%; } 100% { top: 100%; } }
        .scan-sweep { animation: scanSweep 1.6s ease-in-out infinite alternate; }
      `}</style>

      <div className="bg-cyber-grid absolute inset-0 pointer-events-none opacity-40 z-0" />
      <div className="scanlines" />
      
      {notification && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-[1000] bg-red-600/90 backdrop-blur-md text-white px-8 py-3 angle-clip font-black uppercase tracking-[0.3em] text-[10px] shadow-[0_0_30px_red] animate-fade-in flex items-center gap-2 border border-red-400">
          <AlertCircle size={14}/> {notification}
        </div>
      )}

      {/* Global Header HUD */}
      <div className="absolute top-0 inset-x-0 z-50 flex items-center justify-between p-6 pointer-events-none">
        <button onClick={onClose} className="pointer-events-auto p-3 bg-black/60 backdrop-blur-md hover:bg-cyan-500/20 hover:shadow-[0_0_15px_cyan] hover:text-cyan-400 rounded-full transition-all border border-zinc-800 hover:border-cyan-400">
          <X size={20} />
        </button>
        <div className="bg-black/60 backdrop-blur-xl hud-border text-cyan-400 px-6 py-2 angle-clip text-[10px] font-black uppercase tracking-[0.2em] flex items-center gap-3">
          <span className={`w-2 h-2 rounded-full ${gameState === 'intro' ? 'bg-yellow-500 shadow-[0_0_10px_yellow]' : gameState === 'playing' ? 'bg-red-500 shadow-[0_0_10px_red]' : 'bg-cyan-500 shadow-[0_0_10px_cyan]'} animate-pulse`} /> 
          {gameState === 'playing' ? (amISpectator ? 'SPECTATING LIVE' : 'RATING FIT') : gameState === 'intro' ? 'MATCH FOUND' : 'READY'}
        </div>
      </div>

      {/* LOBBY STATE */}
      {gameState === 'lobby' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center p-6 z-40 bg-black animate-fade-in">
          <div className="relative z-10 w-full max-w-sm flex flex-col gap-6 text-center">
            <div>
              <div className="relative inline-block">
                 <Flame size={72} className="text-cyan-400 mx-auto mb-4 drop-shadow-[0_0_25px_cyan] animate-pulse" />
                 <div className="absolute inset-0 bg-cyan-400 blur-[50px] opacity-20 rounded-full" />
              </div>
              <h1 className="text-5xl font-black italic tracking-tighter uppercase mb-2 neon-text-cyan">Thread For Thread</h1>
              <p className="text-cyan-400/80 font-mono text-[10px] uppercase tracking-[0.4em] mb-4">Style For Style</p>
              <div className="h-px w-32 bg-gradient-to-r from-transparent via-cyan-500 to-transparent mx-auto opacity-50" />
            </div>
            
            <div className="flex flex-col gap-4 mt-8">
              <button onClick={() => setGameState('setup')} className="w-full py-5 bg-cyan-500/10 text-cyan-400 hud-border angle-clip font-black uppercase tracking-[0.3em] text-xs transition-all flex items-center justify-center gap-3 hover:bg-cyan-400 hover:text-black hover:shadow-[0_0_40px_rgba(34,211,238,0.6)]">
                <ShirtIcon size={18} /> Rate My Fit
              </button>
              <button onClick={handleSpectate} className="w-full py-5 bg-black/50 border border-zinc-800 text-zinc-400 angle-clip font-black uppercase tracking-[0.3em] text-xs transition-all flex items-center justify-center gap-3 hover:bg-zinc-900 hover:text-white hover:border-zinc-500">
                <Eye size={18} /> Spectate Feed
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SETUP STATE */}
      {gameState === 'setup' && (
        <div className="absolute inset-0 flex flex-col justify-center p-8 z-40 animate-fade-in bg-[#050505]">
          <h2 className="text-3xl font-black uppercase italic mb-8 neon-text-cyan text-center">Setup Match</h2>
          <div className="space-y-8 max-w-md mx-auto w-full relative z-10">
            <div>
              <p className="text-[10px] font-mono text-cyan-500/60 uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
                 <span className="w-1 h-1 bg-cyan-500 rounded-full" /> Input Source
              </p>
              <div className="grid grid-cols-2 gap-4">
                <button onClick={() => setShowCameraConsent(true)} className={`relative flex flex-col items-center p-6 angle-clip transition-all ${inputMode === 'camera' ? 'bg-cyan-500/20 text-cyan-400 hud-border' : 'border border-zinc-800 bg-black/50 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200'}`}>
                  <Camera size={24} className="mb-2" />
                  <span className="font-black text-[10px] uppercase tracking-widest">Live Optics</span>
                </button>
                <label className={`relative flex flex-col items-center p-6 angle-clip cursor-pointer transition-all ${inputMode === 'upload' ? 'bg-cyan-500/20 text-cyan-400 hud-border' : 'border border-zinc-800 bg-black/50 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200'}`}>
                  <Upload size={24} className="mb-2" />
                  <span className="font-black text-[10px] uppercase tracking-widest">Data Upload</span>
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                </label>
              </div>
              
              {((userMediaStream && inputMode === 'camera') || (userPhotoUrl && inputMode === 'upload')) && (
                <div className="mt-6 relative overflow-hidden hud-border angle-clip animate-fade-in bg-black">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent opacity-50 z-10" />
                  {inputMode === 'camera' && <video ref={videoRef} autoPlay playsInline muted className="w-full h-56 object-cover scale-x-[-1] filter contrast-125 saturate-150" />}
                  {inputMode === 'upload' && <img src={userPhotoUrl!} className="w-full h-56 object-cover filter contrast-125 saturate-150" alt="Uploaded" />}
                  <div className="absolute bottom-2 left-2 bg-black/80 px-2 py-1 text-[8px] font-mono text-cyan-400 border border-cyan-500/50 uppercase tracking-widest z-10">SYS.READY</div>
                </div>
              )}
            </div>
            
            {inputMode && (
              <div className="animate-fade-in">
                <button onClick={() => startMatchmaking(false)} className="w-full py-5 bg-cyan-400 text-black angle-clip font-black uppercase text-xs tracking-[0.3em] hover:bg-white hover:shadow-[0_0_30px_cyan] transition-all flex justify-between px-8 items-center group relative overflow-hidden">
                  <span className="relative z-10">Find Match</span>
                  <span className="flex items-center gap-2 relative z-10">
                    <span className="w-2 h-2 bg-red-600 rounded-full animate-pulse shadow-[0_0_10px_red]" />
                    <ChevronRight size={16} className="transform group-hover:translate-x-1 transition-transform" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-300 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MATCHING STATE */}
      {gameState === 'matching' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#050505] z-40 p-6 animate-fade-in relative">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(34,211,238,0.1)_0%,transparent_60%)] pointer-events-none" />
          
          <div className="relative z-10 flex flex-col items-center justify-center w-full max-w-sm mt-24">
            <div className="relative flex items-center justify-center w-40 h-40 mb-10">
              <div className="absolute inset-0 border border-cyan-500/30 rounded-full animate-ping-slow shadow-[0_0_30px_rgba(34,211,238,0.2)]" />
              <div className="absolute inset-4 border border-cyan-400/50 rounded-full animate-[spin_3s_linear_infinite]" style={{ borderTopColor: 'transparent', borderLeftColor: 'transparent' }} />
              <Crosshair size={56} className="text-cyan-400 animate-pulse relative z-10 drop-shadow-[0_0_15px_cyan]" />
            </div>
            
            <h2 className="text-2xl font-black uppercase tracking-[0.3em] mb-2 neon-text-cyan text-center">
              {matchData?.status === 'waiting' ? "Hmm..." : 'Searching...'}
            </h2>
            <p className="text-cyan-500 font-mono text-[10px] uppercase tracking-widest animate-pulse mb-12 text-center">
              {matchData?.status === 'waiting' ? "No one's online right now. You'll be matched the moment someone joins." : 'Finding a random player...'}
            </p>
            
            <button onClick={resetGame} className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500 hover:text-red-500 border-b border-zinc-700 hover:border-red-500 pb-1 transition-colors relative z-10">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* INTRO VS STATE */}
      {gameState === 'intro' && matchData && matchData.userB && (
        <div className="absolute inset-0 flex w-full h-full z-50 bg-[#050505] overflow-hidden">
          <div className="flex-1 relative animate-slide-right border-r-4 border-cyan-500/50">
            <img src={matchData.userA.media} className="w-full h-full object-cover filter brightness-[0.8] contrast-[1.3] sepia-[0.2] hue-rotate-180" alt="User A" />
            <div className="absolute inset-0 bg-gradient-to-t from-cyan-900/90 via-transparent to-black/50 mix-blend-multiply" />
            <div className="absolute bottom-1/4 left-8 text-left z-10">
              <div className="bg-cyan-500/20 border border-cyan-400 px-3 py-1 angle-clip inline-block mb-3">
                <p className="text-cyan-400 font-mono text-[10px] tracking-widest uppercase font-bold">Player 01</p>
              </div>
              <h2 className="text-5xl md:text-7xl font-black italic uppercase text-white neon-text-cyan tracking-tighter leading-none">{matchData.userA.name}</h2>
            </div>
          </div>
          <div className="flex-1 relative animate-slide-left border-l-4 border-yellow-500/50">
            <img src={matchData.userB.media} className="w-full h-full object-cover filter brightness-[0.8] contrast-[1.3] sepia-[0.3]" alt="User B" />
            <div className="absolute inset-0 bg-gradient-to-t from-yellow-900/90 via-transparent to-black/50 mix-blend-multiply" />
            <div className="absolute bottom-1/4 right-8 text-right z-10">
              <div className="bg-yellow-500/20 border border-yellow-400 px-3 py-1 angle-clip inline-block mb-3">
                <p className="text-yellow-400 font-mono text-[10px] tracking-widest uppercase font-bold">Challenger</p>
              </div>
              <h2 className="text-5xl md:text-7xl font-black italic uppercase text-white neon-text-yellow tracking-tighter leading-none">{matchData.userB.name}</h2>
            </div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-2 bg-white h-full absolute opacity-50 shadow-[0_0_30px_white]" />
            <div className="animate-slam-in bg-black hex-clip p-6 border-2 border-white shadow-[0_0_50px_rgba(255,255,255,1)] z-20 flex items-center justify-center min-w-[150px] min-h-[150px]">
              <h1 className="text-7xl md:text-8xl font-black italic uppercase text-white drop-shadow-[0_0_15px_white]">
                VS
              </h1>
            </div>
          </div>
        </div>
      )}

      {/* PLAYING STATE — outfit scan + staged rating reveal */}
      {gameState === 'playing' && matchData && matchData.userB && (
        <div className="flex-1 flex flex-col h-full relative animate-fade-in bg-[#050505]">

          <div className="flex-1 flex w-full relative">
            {/* LEFT SIDE (Player A) */}
            <div className="flex-1 relative overflow-hidden border-r-2 border-zinc-900">
              <img src={matchData.userA.media} className="w-full h-full object-cover filter contrast-[1.1] saturate-[1.1]" alt="User A" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-black/50 pointer-events-none" />

              {revealStage === 'scanning' && (
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  <div className="absolute left-0 right-0 h-24 bg-gradient-to-b from-transparent via-cyan-400/30 to-transparent scan-sweep" />
                </div>
              )}

              <div className="absolute top-6 left-6 bg-black/80 backdrop-blur-md px-5 py-2 angle-clip border border-cyan-400/50 flex items-center gap-3 z-30 shadow-[0_0_15px_rgba(34,211,238,0.2)]">
                <span className="font-black text-xs uppercase tracking-[0.2em] text-cyan-400">{matchData.userA.name}</span>
                {user && matchData.userA.uid === user.uid && <span className="text-[9px] text-black bg-cyan-400 px-1 font-bold">YOU</span>}
              </div>

              <div className="absolute bottom-6 left-6 right-6 z-30 bg-black/85 backdrop-blur-md border border-cyan-500/40 angle-clip p-4">
                {revealStage === 'scanning' && (
                  <div className="flex items-center gap-2 text-cyan-400 font-mono text-[10px] uppercase tracking-widest">
                    <Scan size={14} className="animate-pulse" /> Scanning fit...
                  </div>
                )}
                {revealStage !== 'scanning' && matchData.ratingA && (
                  <div className="flex flex-col gap-1 font-mono text-[11px] uppercase tracking-widest">
                    <div className="flex justify-between text-cyan-300"><span>Shirt</span><span className="font-black">{matchData.ratingA.shirt}/10</span></div>
                    <div className="flex justify-between text-cyan-300"><span>Pants</span><span className="font-black">{matchData.ratingA.pants}/10</span></div>
                    {revealStage === 'full' && (
                      <div className="flex justify-between text-white pt-1 mt-1 border-t border-cyan-500/30 text-sm">
                        <span>Full Fit</span><span className="font-black neon-text-cyan">{matchData.ratingA.final}/10</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT SIDE (Player B) */}
            <div className="flex-1 relative overflow-hidden border-l-2 border-zinc-900">
              <img src={matchData.userB.media} className="w-full h-full object-cover filter contrast-[1.1] saturate-[1.1]" alt="User B" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-black/50 pointer-events-none" />

              {revealStage === 'scanning' && (
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  <div className="absolute left-0 right-0 h-24 bg-gradient-to-b from-transparent via-yellow-400/30 to-transparent scan-sweep" />
                </div>
              )}

              <div className="absolute top-6 right-6 bg-black/80 backdrop-blur-md px-5 py-2 angle-clip border border-yellow-400/50 flex items-center gap-3 z-30 shadow-[0_0_15px_rgba(250,204,21,0.2)]">
                {user && matchData.userB.uid === user.uid && <span className="text-[9px] text-black bg-yellow-400 px-1 font-bold">YOU</span>}
                <span className="font-black text-xs uppercase tracking-[0.2em] text-yellow-400">{matchData.userB.name}</span>
              </div>

              <div className="absolute bottom-6 left-6 right-6 z-30 bg-black/85 backdrop-blur-md border border-yellow-500/40 angle-clip p-4">
                {revealStage === 'scanning' && (
                  <div className="flex items-center justify-end gap-2 text-yellow-400 font-mono text-[10px] uppercase tracking-widest">
                    Scanning fit... <Scan size={14} className="animate-pulse" />
                  </div>
                )}
                {revealStage !== 'scanning' && matchData.ratingB && (
                  <div className="flex flex-col gap-1 font-mono text-[11px] uppercase tracking-widest">
                    <div className="flex justify-between text-yellow-300"><span>Shirt</span><span className="font-black">{matchData.ratingB.shirt}/10</span></div>
                    <div className="flex justify-between text-yellow-300"><span>Pants</span><span className="font-black">{matchData.ratingB.pants}/10</span></div>
                    {revealStage === 'full' && (
                      <div className="flex justify-between text-white pt-1 mt-1 border-t border-yellow-500/30 text-sm">
                        <span>Full Fit</span><span className="font-black neon-text-yellow">{matchData.ratingB.final}/10</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* CYBER TIMER HUD */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-30 flex flex-col items-center justify-center w-28 h-28 bg-black/90 backdrop-blur-xl hex-clip border-2 border-zinc-700 shadow-[0_0_40px_rgba(0,0,0,0.9)]">
              <div className="absolute inset-0 hex-clip border-[3px] border-white/10 m-1" />
              <div className={`text-4xl font-black italic tracking-tighter z-10 ${timeLeft <= 5 ? 'neon-text-red text-white animate-pulse' : 'text-white drop-shadow-[0_0_10px_white]'}`}>
                {timeLeft}
              </div>
              <div className="text-[7px] font-mono uppercase tracking-[0.3em] text-zinc-500 mt-1 z-10">Secs</div>
            </div>
          </div>

          {/* CHAT LOG */}
          <div className="absolute bottom-32 left-6 right-6 h-40 overflow-y-auto pointer-events-none z-40 flex flex-col justify-end gap-2 pb-2 scrollbar-hide">
            {(matchData?.comments || []).map((msg, i) => (
              <div key={i} className="bg-black/90 backdrop-blur-md px-4 py-3 angle-clip w-fit max-w-[80%] border-l-2 border-cyan-500 pointer-events-auto shadow-[0_4px_20px_rgba(0,0,0,0.5)] animate-slide-right">
                <span className="font-black text-[9px] text-cyan-400 uppercase tracking-[0.2em] block mb-1 opacity-80">{msg.user}</span>
                <span className="text-sm font-bold text-zinc-100">{msg.text}</span>
              </div>
            ))}
          </div>

          {/* INPUT HUD */}
          <div className="absolute bottom-6 left-6 right-6 z-50 flex flex-col gap-3 max-w-3xl mx-auto w-full">
            <div className="flex gap-2 justify-center mb-1">
              {['🔥', '💀', '🥶', '💯', '🧢'].map(emoji => (
                <button key={emoji} onClick={() => handleSendComment(undefined, emoji)} className="w-12 h-10 bg-black/80 backdrop-blur-md border border-zinc-800 angle-clip flex items-center justify-center text-lg hover:bg-cyan-500/20 hover:border-cyan-400 transition-all hover:shadow-[0_0_15px_cyan]">
                  {emoji}
                </button>
              ))}
            </div>
            <form onSubmit={handleSendComment} className="flex-1 relative w-full flex gap-2">
              <input type="text" value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="REACT TO THE FIT..." className="w-full bg-black/90 backdrop-blur-xl border border-zinc-700 text-white angle-clip pl-6 pr-4 py-4 text-xs font-mono font-bold focus:outline-none focus:border-cyan-400 focus:shadow-[inset_0_0_15px_rgba(34,211,238,0.2)] transition-all uppercase placeholder-zinc-600" />
              <button type="submit" disabled={!newComment.trim()} className="w-16 bg-cyan-400 text-black angle-clip flex items-center justify-center hover:bg-white transition-colors disabled:opacity-50 shadow-[0_0_15px_cyan]">
                <Send size={18} />
              </button>
            </form>
          </div>
        </div>
      )}

      {/* RESULT STATE */}
      {gameState === 'result' && matchData && matchData.userB && user && matchData.ratingA && matchData.ratingB && (
        <div className="absolute inset-0 z-50 bg-[#050505] flex flex-col p-6 overflow-y-auto animate-fade-in relative">
          <div className="bg-cyber-grid absolute inset-0 pointer-events-none opacity-30 z-0" />
          
          <div className="relative z-10 flex flex-col items-center flex-1 w-full max-w-3xl mx-auto mt-10">
            {(() => {
              const amIUserA = matchData.userA.uid === user.uid;
              const myRating = amIUserA ? matchData.ratingA! : matchData.ratingB!;
              const theirRating = amIUserA ? matchData.ratingB! : matchData.ratingA!;
              const opponentName = amIUserA ? matchData.userB!.name : matchData.userA.name;
              
              const iWon = myRating.final > theirRating.final;
              const isTie = myRating.final === theirRating.final;

              const ratingCard = (rating: OutfitRating, label: string, colorClass: string, glow: boolean) => (
                <div className={`flex-1 bg-black/80 backdrop-blur-xl border-2 angle-clip p-6 text-center flex flex-col justify-center ${glow ? `${colorClass === 'cyan' ? 'border-cyan-400 shadow-[0_0_40px_rgba(34,211,238,0.3)]' : 'border-yellow-400 shadow-[0_0_40px_rgba(250,204,21,0.3)]'}` : 'border-zinc-800'}`}>
                  <p className="text-zinc-500 font-mono uppercase tracking-[0.3em] text-[10px] mb-3">{label}</p>
                  <p className={`text-5xl font-black italic mb-3 ${glow ? (colorClass === 'cyan' ? 'neon-text-cyan text-white' : 'neon-text-yellow text-white') : 'text-zinc-300'}`}>{rating.final}<span className="text-lg">/10</span></p>
                  <div className="flex flex-col gap-1 font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                    <div className="flex justify-between"><span>Shirt</span><span className="text-zinc-200 font-bold">{rating.shirt}/10</span></div>
                    <div className="flex justify-between"><span>Pants</span><span className="text-zinc-200 font-bold">{rating.pants}/10</span></div>
                    <div className="flex justify-between"><span>Full Fit</span><span className="text-zinc-200 font-bold">{rating.fullFit}/10</span></div>
                  </div>
                </div>
              );

              return (
                <>
                  <div className="text-center mb-10 animate-fade-in">
                    {amISpectator ? (
                       <h1 className="text-5xl md:text-7xl font-black italic tracking-tighter uppercase leading-none neon-text-cyan text-white">
                          BATTLE<br/> <span className="text-3xl md:text-5xl">RATED</span>
                       </h1>
                    ) : (
                      <h1 className={`text-6xl md:text-8xl font-black italic tracking-tighter uppercase leading-none ${isTie ? 'text-zinc-300 drop-shadow-[0_0_20px_gray]' : iWon ? 'neon-text-cyan text-white' : 'neon-text-red text-white'}`}>
                        {isTie ? 'DRAW' : iWon ? 'VICTORY' : 'DEFEAT'}<br/> 
                        <span className="text-white text-3xl md:text-5xl opacity-80" style={{ textShadow: 'none' }}>
                          {isTie ? `VS @${opponentName}` : iWon ? `AGAINST @${opponentName}` : `BY @${opponentName}`}
                        </span>
                      </h1>
                    )}
                  </div>

                  <div className="flex justify-center items-stretch gap-4 md:gap-8 mb-10 w-full animate-fade-in">
                    {amISpectator ? (
                      <>
                        {ratingCard(matchData.ratingA!, matchData.userA.name, 'cyan', matchData.ratingA!.final > matchData.ratingB!.final)}
                        <div className="text-zinc-800 font-black italic text-4xl self-center px-2">VS</div>
                        {ratingCard(matchData.ratingB!, matchData.userB!.name, 'yellow', matchData.ratingB!.final > matchData.ratingA!.final)}
                      </>
                    ) : (
                      <>
                        {ratingCard(myRating, 'YOU', 'cyan', iWon)}
                        <div className="text-zinc-800 font-black italic text-4xl self-center px-2">VS</div>
                        {ratingCard(theirRating, 'OPPONENT', 'yellow', !iWon && !isTie)}
                      </>
                    )}
                  </div>

                  {!amISpectator && (
                    <div className={`font-mono text-xs font-bold uppercase tracking-widest py-2 px-4 angle-clip inline-block mx-auto mb-8 ${isTie ? 'text-zinc-400 bg-zinc-800' : iWon ? 'text-black bg-cyan-400 shadow-[0_0_15px_cyan]' : 'text-red-400 border border-red-500/50 bg-red-900/20'}`}>
                      {isTie ? '+0 ELO' : iWon ? 'RATING +26' : 'RATING -26'}
                    </div>
                  )}
                </>
              );
            })()}

            <div className="w-full max-w-md mx-auto flex flex-col gap-4 mb-8">
              <button onClick={() => showToast("Network Link Copied.")} className="w-full py-4 bg-transparent border border-cyan-500/50 text-cyan-400 angle-clip font-black uppercase text-xs tracking-[0.3em] hover:bg-cyan-500/10 hover:shadow-[inset_0_0_20px_rgba(34,211,238,0.2)] transition-all flex items-center justify-center gap-3">
                <Share2 size={16} /> Broadcast Result
              </button>
              {!amISpectator ? (
                <button onClick={nextBattle} className="w-full py-5 bg-cyan-400 text-black angle-clip font-black uppercase text-sm tracking-[0.3em] hover:bg-white hover:shadow-[0_0_30px_cyan] transition-all flex items-center justify-center gap-2">
                  Next Battle <ChevronRight size={18} />
                </button>
              ) : (
                <button onClick={handleSpectate} className="w-full py-5 bg-cyan-400 text-black angle-clip font-black uppercase text-sm tracking-[0.3em] hover:bg-white hover:shadow-[0_0_30px_cyan] transition-all">
                  Watch Next Battle
                </button>
              )}
              <button onClick={resetGame} className="w-full py-4 mt-2 text-zinc-500 font-mono text-[10px] uppercase tracking-widest hover:text-white transition-colors">
                Disconnect from Grid
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CAMERA CONSENT MODAL */}
      {showCameraConsent && (
        <div className="absolute inset-0 z-[1000] bg-black/90 backdrop-blur-xl flex items-center justify-center p-6 animate-fade-in">
          <div className="bg-[#050505] border border-cyan-500/50 p-8 max-w-lg w-full angle-clip shadow-[0_0_50px_rgba(34,211,238,0.15)] relative">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent opacity-50" />
            <div className="flex items-center gap-4 mb-6">
              <AlertCircle size={32} className="text-cyan-400" />
              <h3 className="text-xl font-black italic uppercase neon-text-cyan">Security Clearance</h3>
            </div>
            
            <div className="text-zinc-400 text-xs font-mono leading-relaxed space-y-4 mb-8 text-left">
              <p>
                This camera check is a Thread For Thread session gate for anti-abuse and age acknowledgment. It is not a government ID check or a durable proof of identity.
              </p>
              <p>
                Your camera is used only to capture a single still photo of your outfit for this match. That photo is stored with the match record; no video or continuous stream is transmitted or stored.
              </p>
              <p>
                By starting the check you agree to the Privacy Policy and Terms of Service.
              </p>
            </div>

            <div className="flex gap-4">
              <button 
                onClick={() => setShowCameraConsent(false)} 
                className="flex-1 py-4 bg-transparent border border-zinc-700 text-zinc-400 angle-clip font-black uppercase text-xs tracking-widest hover:text-white hover:border-zinc-500 transition-colors"
              >
                Decline
              </button>
              <button 
                onClick={() => {
                  setShowCameraConsent(false);
                  startCamera();
                }} 
                className="flex-1 py-4 bg-cyan-400 text-black angle-clip font-black uppercase text-xs tracking-widest hover:bg-white hover:shadow-[0_0_20px_cyan] transition-all"
              >
                I Agree & Init
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};